import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PartyPopper, Users, Ticket, Wallet, MapPin, CalendarClock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { getEventHomeSummary, EventHomeSummary, fmtEventDate, money } from '../events/api';
import { readCache, writeCache } from '../../services/localCache';

/** Cuenta regresiva legible. */
function countdown(days: number): string {
  if (days < 0) return 'en curso';
  if (days === 0) return '¡es hoy!';
  if (days === 1) return 'mañana';
  return `faltan ${days} días`;
}

type CachedEvent = { ev: EventHomeSummary | null; revenue: { count: number; total: number } | null };

export default function EventStatusPanel() {
  // Pinta al instante lo último conocido (caché local) y refresca por detrás.
  const cached = readCache<CachedEvent>('event-home');
  const [ev, setEv] = useState<EventHomeSummary | null>(cached?.ev ?? null);
  const [revenue, setRevenue] = useState<CachedEvent['revenue']>(cached?.revenue ?? null);
  const [loaded, setLoaded] = useState(!!cached);
  // Ingresos TAPADOS por defecto, igual que «Pagos del día» (Mabel 28-oct-2026, #1592): el Inicio se ve en el mostrador.
  const [verIngresos, setVerIngresos] = useState(false);

  useEffect(() => {
    getEventHomeSummary()
      .then((r) => {
        setEv(r.data); setRevenue(r.revenue || null);
        writeCache('event-home', { ev: r.data, revenue: r.revenue || null });
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  // Sin evento próximo → no renderizamos nada (el HOME no se llena de huecos).
  if (!loaded || !ev) return null;

  // Vendidos: en festival son las ventas del boletaje (lo manda el backend); en simple, los confirmados de siempre.
  const festival = ev.ticket_mode === 'festival';
  const vendidos = ev.sold ?? ev.going;
  const disponibles = ev.available ?? (ev.capacity ? Math.max(0, ev.capacity - vendidos) : null);
  const pct = ev.capacity ? Math.min(100, Math.round((vendidos / ev.capacity) * 100)) : 0;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-line bg-surface p-5 shadow-sm">
      <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-accent/10 blur-2xl" />
      <div className="relative">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-content">
            <PartyPopper size={16} className="text-accent-ink" /> Próximo evento
          </h3>
          <Link to={`/eventos/${ev.event_id}`} className="shrink-0 text-xs font-medium text-brand-blue hover:underline">
            Ver evento →
          </Link>
        </div>

        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-display text-lg font-bold text-content">{ev.name}</p>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-content-3">
              <CalendarClock size={13} /> {fmtEventDate(ev.datetime)}
            </p>
            {ev.venue && (
              <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-content-3"><MapPin size={13} /> {ev.venue}</p>
            )}
          </div>
          <span className="shrink-0 rounded-full bg-accent/15 px-2.5 py-1 text-xs font-semibold text-accent-ink">
            {countdown(ev.days_until)}
          </span>
        </div>

        {/* Boletos vendidos y disponibles ARRIBA (Mabel 28-oct-2026, #1592): es lo que miran en el mostrador */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-surface-2 px-3 py-2">
            <p className="flex items-center gap-1 text-[11px] text-content-3">
              {festival ? <Ticket size={12} /> : <Users size={13} />} {festival ? 'Boletos vendidos' : 'Confirmados'}
            </p>
            <p className="font-display text-base font-bold tabular-nums text-content" data-testid="evento-home-vendidos">
              {vendidos}{ev.capacity ? <span className="text-xs font-medium text-content-3"> / {ev.capacity}</span> : null}
            </p>
          </div>
          <div className="rounded-xl bg-surface-2 px-3 py-2">
            <p className="text-[11px] text-content-3">Disponibles</p>
            <p className="font-display text-base font-bold tabular-nums text-content" data-testid="evento-home-disponibles">
              {disponibles != null ? disponibles : '—'}
            </p>
          </div>
        </div>
        {ev.capacity ? (
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface-2" title={`${pct}% del cupo`}>
            <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${pct}%` }} />
          </div>
        ) : null}

        {/* Abajo: ingresos con ojito (tapados hasta tocarlos) + precio del boleto */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setVerIngresos((v) => !v)} data-testid="evento-home-ingresos" aria-pressed={verIngresos}
            title={verIngresos ? 'Ocultar ingresos' : 'Ver ingresos'}
            className="rounded-xl bg-surface-2 px-3 py-2 text-left transition hover:bg-line/40">
            <p className="flex items-center gap-1 text-[11px] text-content-3">
              <Wallet size={12} /> Ingresos {verIngresos ? <EyeOff size={11} /> : <Eye size={11} />}
            </p>
            <p className="font-display text-base font-bold tabular-nums text-brand-blue">{verIngresos ? money(revenue?.total || 0) : '$ ****'}</p>
          </button>
          <div className="rounded-xl bg-surface-2 px-3 py-2">
            <p className="text-[11px] text-content-3">Boleto</p>
            <p className="font-display text-base font-bold text-content">{ev.price ? money(ev.price) : '—'}</p>
          </div>
        </div>

        <Link to={`/eventos/${ev.event_id}`}
          className="mt-3 flex items-center justify-center gap-1 rounded-xl border border-line py-2 text-xs font-medium text-content transition hover:border-accent/40 hover:text-accent-ink">
          Administrar evento <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}
